import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Button, Empty, Spin, Tooltip } from 'antd';
import { useAlimtalkSendForm } from '../../../hooks/alimtalk/useAlimtalkSendForm';
import { TemplateSelector } from './TemplateSelector';
import { CsvUploadSection } from './CsvUploadSection';
import { ScheduleSection } from './ScheduleSection';
import { CouponIssueSection } from './CouponIssueSection';
import { CouponExpireSection } from './CouponExpireSection';
import { EventSelectSection } from './EventSelectSection';
import { TestSendModal } from './TestSendModal';
import { PreviewConfirmModal } from './PreviewConfirmModal';
import * as Styled from './style';

export const AlimtalkSendCreateWrap = () => {
  const {
    methods,
    templateList,
    isTemplateListLoading,
    selectedTemplate,
    requirements,
    previewVariables,
    isTemplateError,
    refetchTemplate,
    handleUploadCsv,
    isUploading,
    handleDownloadCsvSample,
    isSampleDownloading,
    handleRegister,
    isPreviewLoading,
    previewState,
    handleRetryPreview,
    handleConfirmRegister,
    closePreview,
    validateBeforeTestSend,
    handleTestSend,
    isRegistering,
    isTesting,
  } = useAlimtalkSendForm();

  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  const templateId = methods.watch('templateId');
  const canTestSend = requirements?.canTestSend ?? false;
  // 유형 변경 시 섹션을 리마운트해 로컬 state(선택 쿠폰/기획전)를 폼 리셋과 함께 초기화한다.
  const sectionKey = templateId ?? 'none';

  // 점진적 노출: 유형 미선택 → 안내 / 상세 로딩 → 스피너 / 선택 완료 → 설정 폼
  const renderBody = () => {
    if (templateId == null) {
      return (
        <Styled.Section>
          <Styled.GuideCenter>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <>
                  <Styled.GuideTitle>발송 유형을 선택해주세요</Styled.GuideTitle>
                  <Styled.GuideSub>유형을 선택하면 쿠폰·대상·예약 설정이 표시됩니다.</Styled.GuideSub>
                </>
              }
            />
          </Styled.GuideCenter>
        </Styled.Section>
      );
    }

    if (isTemplateError) {
      return (
        <Styled.Section>
          <Styled.GuideCenter>
            <Styled.GuideTitle>유형 정보를 불러오지 못했습니다.</Styled.GuideTitle>
            <Button onClick={refetchTemplate}>다시 시도</Button>
          </Styled.GuideCenter>
        </Styled.Section>
      );
    }

    if (!requirements) {
      return (
        <Styled.Section>
          <Styled.GuideCenter>
            <Spin />
            <Styled.GuideSub>유형 정보를 불러오는 중…</Styled.GuideSub>
          </Styled.GuideCenter>
        </Styled.Section>
      );
    }

    return (
      <>
        {requirements.needsCsvVariables && selectedTemplate && (
          <Styled.Section>
            <Styled.SectionTitle>CSV 변수</Styled.SectionTitle>
            <Styled.Hint>CSV 헤더에 다음 컬럼이 필요합니다: userId, {selectedTemplate.variables.join(', ')}</Styled.Hint>
          </Styled.Section>
        )}

        {requirements.isExpireReorderable && <CouponExpireSection key={`expire-${sectionKey}`} />}
        {requirements.needsCouponList && !requirements.isExpireReorderable && (
          <CouponIssueSection
            key={`issue-${sectionKey}`}
            needsAlimtalkCouponId={requirements.needsAlimtalkCouponId}
            needsValidityDates={requirements.needsValidityDates}
          />
        )}
        {requirements.needsEventId && <EventSelectSection key={`event-${sectionKey}`} />}

        {requirements.needsCsvUpload ? (
          <CsvUploadSection
            onUpload={handleUploadCsv}
            isUploading={isUploading}
            isCsvMode={requirements.needsCsvVariables}
            onDownloadSample={handleDownloadCsvSample}
            isSampleDownloading={isSampleDownloading}
          />
        ) : (
          // EXPIRE — CSV 없이 쿠폰 보유자 전원이 자동 대상 (#4841). 대상 수는 등록 확인 모달(preview)에서 표시
          <Styled.Section>
            <Styled.SectionTitle>발송 대상</Styled.SectionTitle>
            <Styled.Hint>
              선택한 쿠폰의 보유자(미사용·미만료) 전원에게 자동 발송됩니다 — CSV 업로드가 필요 없습니다.
            </Styled.Hint>
          </Styled.Section>
        )}
        <ScheduleSection />

        <Styled.ActionBar>
          {/* antd v5 Tooltip은 disabled Button 위에서 안 뜨므로 span으로 감싼다 */}
          <Tooltip title={canTestSend ? '' : '알림톡 메시지가 없는 유형은 테스트 발송할 수 없습니다.'}>
            <span>
              {/* 테스트도 등록과 동일한 전체 payload(CSV·메모 포함) — 모달 열기 전 사전 검증으로 헛걸음 방지 */}
              <Button
                disabled={!canTestSend || isTesting}
                onClick={() => {
                  if (validateBeforeTestSend()) {
                    setIsTestModalOpen(true);
                  }
                }}
              >
                테스트 발송
              </Button>
            </span>
          </Tooltip>
          {/* 1단계: preview(dry-run) 확인 모달 → 2단계: 모달의 [등록] */}
          <Button type="primary" loading={isPreviewLoading || isRegistering} onClick={handleRegister}>
            예약 등록
          </Button>
        </Styled.ActionBar>
      </>
    );
  };

  return (
    <FormProvider {...methods}>
      <Styled.AlimtalkSendCreateWrapStyle>
        <TemplateSelector templateList={templateList} isLoading={isTemplateListLoading} />
        {renderBody()}

        <TestSendModal
          open={isTestModalOpen}
          template={selectedTemplate}
          previewVariables={previewVariables}
          onClose={() => setIsTestModalOpen(false)}
          onSend={(phone) => {
            // 검증 실패(토스트) 시 모달 유지, 발송 시작 시에만 닫는다
            if (handleTestSend(phone)) {
              setIsTestModalOpen(false);
            }
          }}
        />

        <PreviewConfirmModal
          previewState={previewState}
          previewVariables={previewVariables}
          isRetrying={isPreviewLoading}
          isRegistering={isRegistering}
          onRetry={handleRetryPreview}
          onConfirm={handleConfirmRegister}
          onClose={closePreview}
        />
      </Styled.AlimtalkSendCreateWrapStyle>
    </FormProvider>
  );
};
